/**
 * PATH: packages/ui/src/components/settings/AiUsageEventTable.tsx
 * FILE_ID: UI-SETTINGS-AIUSAGE-TABLE-001
 * PURPOSE:
 *   Presentational "Recent inference events" section of the AI usage
 *   dashboard: result-count header, empty state, the event table, and
 *   pager. Markup is byte-identical to the pre-split inline block.
 * WHY:
 *   Extracted from AiUsageDashboard.tsx (god-component split) to keep
 *   the orchestrator under the line limit. Pure props-in / JSX-out.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────┐   ┌──────────────┐
 *   │ rows + pager │ → │ table / empty UI │ → │ rendered DOM │
 *   └──────────────┘   └──────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - lucide-react: row/pager icons
 *   - ./aiUsageFormat: formatNumber/formatTime/formatUSD
 *   - ./types: AiUsageEventRow / AiUsagePagination
 * RELATED:
 *   - AiUsageDashboard.tsx: parent
 */
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatNumber, formatTime, formatUSD } from './aiUsageFormat'
import type { AiUsageEventRow, AiUsagePagination } from './types'

export interface AiUsageEventTableProps {
  rows: AiUsageEventRow[]
  pagination: AiUsagePagination | undefined
  days: number
  autoRefreshOn: boolean
  autoRefreshIntervalMs: number
  page: number
  loading: boolean
  setDays: (days: number) => void
  setPage: (updater: (p: number) => number) => void
}

export function AiUsageEventTable({
  rows,
  pagination,
  days,
  autoRefreshOn,
  autoRefreshIntervalMs,
  page,
  loading,
  setDays,
  setPage,
}: AiUsageEventTableProps) {
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Recent inference events
        </h4>
        {pagination && (
          <span className="text-[11px] text-slate-400">
            {formatNumber(
              Math.min(
                (pagination.page - 1) * pagination.page_size + 1,
                pagination.total,
              ),
            )}
            –
            {formatNumber(
              Math.min(
                pagination.page * pagination.page_size,
                pagination.total,
              ),
            )}{' '}
            of {formatNumber(pagination.total)}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 dark:border-slate-800 p-6 text-center text-xs text-slate-500 space-y-2">
          <p>
            No AI inference calls in the last <strong>{days} days</strong> for this
            organisation.
          </p>
          <p className="text-slate-400">
            This panel is live{autoRefreshOn ? ` (refreshes every ${Math.round(autoRefreshIntervalMs / 1000)}s)` : ''}
            {' '} -  new calls will appear here automatically and bubble up to the
            top-right notification bell.
          </p>
          {days < 365 && (
            <p>
              Try a longer window:{' '}
              {[90, 365].filter((d) => d > days).map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className="underline text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                >
                  {i > 0 ? ' or ' : ''}{d} days
                </button>
              ))}
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Task</th>
                <th className="text-left px-3 py-2 font-medium">Context</th>
                <th className="text-right px-3 py-2 font-medium">Tokens</th>
                <th className="text-right px-3 py-2 font-medium">Latency</th>
                <th className="text-right px-3 py-2 font-medium">Billed</th>
                <th className="text-center px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                    {formatTime(r.timestamp)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800 dark:text-slate-200">
                      {r.task_label}
                    </div>
                    {r.user_email ? (
                      <div className="text-[10px] text-slate-400">
                        {r.user_email}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {r.context_type ? (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">
                        {r.context_type}
                      </span>
                    ) : null}
                    <span className="font-mono">{r.context_id || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatNumber(r.tokens_total)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">
                    {r.latency_ms}ms
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatUSD(r.cost_usd_billed)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.success ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border border-emerald-300 text-emerald-700 dark:text-emerald-400">
                        ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border border-rose-300 text-rose-700 dark:text-rose-400">
                        err
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.total > pagination.page_size && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </button>
          <span className="text-xs text-slate-500">
            Page {pagination.page}
          </span>
          <button
            type="button"
            disabled={!pagination.has_next || loading}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
